import { NOTABLE_LABELS_CONFIG, AssessmentType } from '../config/notableLabels';

export interface NotableLabel {
  field: string;
  label: string;
  value: string;
}

/**
 * Get the list of notable labels for an assessment
 * @param assessmentType - The type of assessment (explain_review_admit_dc, explain_review_revisit, explain_review_edd)
 * @param assessmentData - The assessment data object containing field values
 * @returns Array of notable labels found in the assessment
 */
export function getNotableLabels(
  assessmentType: AssessmentType,
  assessmentData: Record<string, any>
): NotableLabel[] {
  const config = NOTABLE_LABELS_CONFIG[assessmentType];
  if (!config || !assessmentData) {
    return [];
  }

  const notableLabels: NotableLabel[] = [];

  for (const [field, fieldConfig] of Object.entries(config)) {
    const value = assessmentData[field];
    if (value && (fieldConfig.notableValues as readonly string[]).includes(value)) {
      notableLabels.push({
        field,
        label: fieldConfig.label,
        value
      });
    }
  }

  return notableLabels;
}

/**
 * Get the count of notable labels for an assessment
 * @param assessmentType - The type of assessment
 * @param assessmentData - The assessment data object
 * @returns The number of notable labels found
 */
export function getNotableLabelsCount(
  assessmentType: AssessmentType,
  assessmentData: Record<string, any>
): number {
  return getNotableLabels(assessmentType, assessmentData).length;
}

/**
 * Check if a specific field value is notable for an assessment type
 * @param assessmentType - The type of assessment
 * @param field - The field name to check
 * @param value - The value to check
 * @returns True if the value is considered notable
 */
export function isFieldValueNotable(
  assessmentType: AssessmentType,
  field: string,
  value: string | string[] | undefined
): boolean {
  if (!value) return false;

  const config = NOTABLE_LABELS_CONFIG[assessmentType];
  if (!config) return false;

  const fieldConfig = (config as Record<string, { notableValues: readonly string[], label: string }>)[field];
  if (!fieldConfig) return false;

  if (Array.isArray(value)) {
    return value.some(v => (fieldConfig.notableValues as readonly string[]).includes(v));
  }

  return (fieldConfig.notableValues as readonly string[]).includes(value);
}
